import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays } from 'lucide-react';
import { getApiBaseURL } from '../utils/api';

const SAFETY_TERMS = /산업안전|안전보건|중대재해|건설기술|건설산업|소방|위험물|화학물질|시설물.*안전|승강기.*안전|전기안전|가스안전|재난 및 안전관리/;
const SAFETY_DEPARTMENTS = /고용노동부|소방청|국토교통부/;
const DEPARTMENT_SAFETY_TERMS = /안전|보건|건설|소방|위험|재난|시설|교통/;

const displayDate = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 8
    ? `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6, 8)}`
    : value || '날짜 확인 중';
};

export default function WeeklySafetyLawUpdates() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${getApiBaseURL()}/api/laws/updates/recent-by-effective-date?days=30`, {
      signal: controller.signal,
    })
      .then((response) => response.ok ? response.json() : [])
      .then((data) => {
        const rows = Array.isArray(data) ? data : [];
        setItems(rows.filter((row) => (
          SAFETY_TERMS.test(row.law_name || '') || (
            SAFETY_DEPARTMENTS.test(row.department || '') &&
            DEPARTMENT_SAFETY_TERMS.test(row.law_name || '')
          )
        )).slice(0, 4));
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
    return () => controller.abort();
  }, []);

  return (
    <section className="mx-auto max-w-6xl" aria-labelledby="weekly-safety-laws-title">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-emerald-700">놓치지 말아야 할 최신 기준</p>
          <h2 id="weekly-safety-laws-title" className="mt-1 flex items-center gap-2 text-2xl font-black text-slate-900 sm:text-3xl">
            <CalendarDays className="text-emerald-600" /> 최근 30일 시행·개정 기준
          </h2>
          <p className="mt-2 text-sm text-slate-500">최근 시행되거나 개정된 안전 관련 법령을 모았습니다.</p>
        </div>
        <Link to="/safety-regulations" className="inline-flex items-center gap-1 text-sm font-bold text-emerald-700">
          전체 법령 보기 <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      {isLoading ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-10 text-center text-sm text-slate-500 shadow-sm">
          최근 시행·개정 기준을 불러오고 있습니다.
        </div>
      ) : items.length ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item) => (
          <Link
            key={`${item.mst}-${item.effective_date}`}
            to={`/safety-regulations/${item.law_id}`}
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md"
          >
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="rounded-full bg-emerald-50 px-2 py-1 font-bold text-emerald-700">
                {item.revision_type || '개정'}
              </span>
              <span className="text-slate-500">시행 {displayDate(item.effective_date)}</span>
            </div>
            <h3 className="mt-3 line-clamp-2 font-bold leading-6 text-slate-900">{item.law_name}</h3>
            <p className="mt-2 line-clamp-1 text-xs text-slate-500">{item.department}</p>
            </Link>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-10 text-center shadow-sm">
          <p className="font-bold text-slate-700">최근 30일 이내 시행·개정된 안전법령이 없습니다.</p>
          <p className="mt-1 text-sm text-slate-500">새로운 변경사항이 확인되면 이곳에 표시됩니다.</p>
        </div>
      )}
    </section>
  );
}
